import { useEffect, useMemo, useState } from "react";
import { Layout, Menu, theme, Dropdown, Avatar } from "antd";
import { DashboardOutlined } from "@ant-design/icons";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ADMIN_ROLES } from "@glow-price/shared";
import type { MenuProps } from "antd";

import { clearSession, getSessionUser, subscribeAuth } from "../auth/store";
import { useCurrentAdmin } from "../auth/useCurrentAdmin";

const { Header, Sider, Content } = Layout;

// Phase 6D-2 owns this list. 4 业务分组 + 概览。每组用 SubMenu 包起来。
type MenuItem = NonNullable<MenuProps["items"]>[number];
const baseItems: MenuItem[] = [
  // Phase 7: Dashboard is the default landing page (root `/`). Keep the
  // legacy `/dashboard` route working in the router for bookmarks.
  { key: "/", icon: <DashboardOutlined />, label: <Link to="/">概览</Link> },
  { type: "divider" as const },
  {
    key: "group:content",
    label: "内容管理",
    type: "group",
    children: [
      { key: "/brands", label: <Link to="/brands">品牌</Link> },
      { key: "/categories", label: <Link to="/categories">分类</Link> },
      { key: "/standard-products", label: <Link to="/standard-products">标准商品</Link> },
      { key: "/editorial-tags", label: <Link to="/editorial-tags">编辑精选 / 标签</Link> },
      { key: "/home-editorial", label: <Link to="/home-editorial">首页运营</Link> },
    ],
  },
  { type: "divider" as const },
  {
    key: "group:channels",
    label: "渠道与采集",
    type: "group",
    children: [
      { key: "/channels", label: <Link to="/channels">渠道</Link> },
      { key: "/channel-products", label: <Link to="/channel-products">渠道商品</Link> },
      {
        key: "/channel-products-matrix",
        label: <Link to="/channel-products-matrix">渠道商品矩阵</Link>,
      },
      { key: "/channel-scrapers", label: <Link to="/channel-scrapers">采集管理</Link> },
      { key: "/price-snapshots", label: <Link to="/price-snapshots">价格快照</Link> },
      { key: "/flash-sales", label: <Link to="/flash-sales">限时秒杀</Link> },
      { key: "/affiliate-networks", label: <Link to="/affiliate-networks">联盟平台</Link> },
      {
        key: "/channel-affiliate-configs",
        label: <Link to="/channel-affiliate-configs">渠道联盟配置</Link>,
      },
    ],
  },
  { type: "divider" as const },
  {
    key: "group:ops",
    label: "运营",
    type: "group",
    children: [
      { key: "/outbound-clicks", label: <Link to="/outbound-clicks">导购点击</Link> },
      { key: "/affiliate-orders", label: <Link to="/affiliate-orders">联盟订单</Link> },
      {
        key: "/commission-settlements",
        label: <Link to="/commission-settlements">佣金结算</Link>,
      },
      { key: "/audit-logs", label: <Link to="/audit-logs">审计日志</Link> },
    ],
  },
  { type: "divider" as const },
  {
    key: "group:system",
    label: "系统",
    type: "group",
    children: [
      { key: "/legal-docs", label: <Link to="/legal-docs">法务文档</Link> },
      { key: "/system-settings", label: <Link to="/system-settings">系统设置</Link> },
    ],
  },
];

export function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const [user, setUser] = useState(getSessionUser());
  // Phase 6D-1: kick off the /me fetch + cache so RoleGuard / menus can
  // render synchronously from anywhere in the tree.
  const me = useCurrentAdmin();

  useEffect(() => subscribeAuth(() => setUser(getSessionUser())), []);

  // Append the superadmin-only "账号管理" entry at the bottom of the sider
  // without touching the business menu above (Phase 6D-2 owns those).
  const items = useMemo<MenuProps["items"]>(() => {
    if (me?.role !== ADMIN_ROLES.SUPERADMIN) return baseItems;
    return [
      ...baseItems,
      { type: "divider" as const },
      { key: "/admin-users", label: <Link to="/admin-users">账号管理</Link> },
    ];
  }, [me?.role]);

  const userMenuItems: MenuProps["items"] = [
    {
      key: "change-password",
      label: "修改密码",
      onClick: () => navigate("/me/change-password"),
    },
    { type: "divider" as const },
    {
      key: "logout",
      label: "退出登录",
      onClick: () => {
        clearSession();
        navigate("/login");
      },
    },
  ];

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        width={220}
        theme="light"
        style={{ borderRight: `1px solid ${token.colorBorderSecondary}` }}
      >
        <div
          style={{
            padding: "20px 20px 16px",
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <div
            style={{
              fontSize: 10,
              letterSpacing: 2.4,
              textTransform: "uppercase",
              color: token.colorTextTertiary,
            }}
          >
            Editorial Console
          </div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 500,
              letterSpacing: 0.6,
              color: token.colorPrimary,
              marginTop: 4,
            }}
          >
            Glow Price
          </div>
        </div>
        <Menu
          mode="inline"
          items={items}
          selectedKeys={[location.pathname]}
          style={{ borderRight: 0, paddingTop: 8 }}
        />
      </Sider>
      <Layout>
        <Header
          style={{
            background: token.colorBgContainer,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            padding: "0 24px",
          }}
        >
          <Dropdown menu={{ items: userMenuItems }}>
            <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}>
              <Avatar
                size="small"
                style={{ background: token.colorPrimary, color: "#fff" }}
              >
                {(user?.displayName ?? user?.username ?? "?").slice(0, 1)}
              </Avatar>
              <span style={{ letterSpacing: 0.3 }}>
                {user?.displayName ?? user?.username ?? "未登录"}
              </span>
            </span>
          </Dropdown>
        </Header>
        <Content style={{ padding: 24, background: token.colorBgLayout }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
